/**
 * `TeamMemberCertificationDetail` — dettaglio dell'istanza di certificazione di
 * un membro del Team, in sola lettura. È l'equivalente di `MyCertificationDetail`
 * ma per un altro utente: mostra la stessa medaglietta e gli stessi dati di
 * assegnazione (origine, date, badge) della persona indicata.
 *
 * Rotta `/team/:userId/certifications/:certificationId` (sezione "Livelli").
 * Dati: `GET /api/dashboard/users/:userId/certifications/:certificationId/detail`
 * → `MyCertificationDetailDto` (404 se quel membro non possiede la
 * certificazione). L'autorizzazione è imposta dal preHandler `requireLevelsAccess`.
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type { MyCertificationDetailDto } from '../shared/dto';
import { certExpiryStatus, formatCertDate } from '../components/CertificationCard';
import { CertificationMedal } from '../components/CertificationMedal';
import { downloadBadgeImage } from '../utils/downloadBadge';
import {
  Alert,
  Badge,
  Button,
  Icon,
  Loading,
  PageHeader,
} from '../components/ui';

/** Renderizza la scadenza con lo stesso stato/tono della card certificazione. */
function ExpiryValue({ iso }: { iso: string | null }) {
  const status = certExpiryStatus(iso);
  if (status === 'none') return <>Nessuna</>;
  if (status === 'expired') {
    return <Badge tone="danger">{formatCertDate(iso)}</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warning">In scadenza {formatCertDate(iso)}</Badge>;
  }
  return <>{formatCertDate(iso)}</>;
}

export default function TeamMemberCertificationDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { userId, certificationId } = useParams<{
    userId: string;
    certificationId: string;
  }>();

  const { data, isLoading, isError, error } = useQuery<MyCertificationDetailDto>({
    queryKey: ['dashboard', 'users', userId, 'certifications', 'detail', certificationId],
    queryFn: () =>
      api.get<MyCertificationDetailDto>(
        `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/certifications/${encodeURIComponent(certificationId ?? '')}/detail`,
      ),
    enabled:
      userId !== undefined &&
      userId !== '' &&
      certificationId !== undefined &&
      certificationId !== '',
  });

  const backToMember = () => {
    void navigate(`/insights/team/${encodeURIComponent(userId ?? '')}`);
  };

  if (isLoading) {
    return <Loading>Caricamento certificazione…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error
        ? error.message
        : 'Impossibile caricare la certificazione.';
    return (
      <section
        className="certification-detail"
        aria-labelledby="team-member-certification-detail-title"
      >
        <PageHeader
          title="Dettaglio certificazione"
          titleId="team-member-certification-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToMember}>
              ← Torna alla dashboard
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  return (
    <section
      className="certification-detail"
      aria-labelledby="team-member-certification-detail-title"
    >
      <PageHeader
        title="Dettaglio certificazione"
        titleId="team-member-certification-detail-title"
        subtitle="I dettagli della certificazione e dell'assegnazione del membro."
        actions={
          <>
            <Button type="button" variant="ghost" onClick={backToMember}>
              ← Torna alla dashboard
            </Button>
            {data.imageUrl !== undefined && (
              <Button
                type="button"
                variant="default"
                onClick={() => {
                  void downloadBadgeImage(data.imageUrl as string, data.name);
                }}
              >
                <Icon name="download" />
                <span>Scarica il badge</span>
              </Button>
            )}
          </>
        }
      />

      {/* Hero: medaglietta, nome, ente, descrizione, skill e link alla pagina
          generale della certificazione. */}
      <div className="certification-detail__hero">
        <CertificationMedal
          imageUrl={data.imageUrl}
          name={data.name}
          issuer={data.issuer}
          backFields={[
            { label: 'Conseguita', value: formatCertDate(data.issuedAt) },
            {
              label: 'Scadenza',
              value:
                data.expiresAt === null ? 'Nessuna' : formatCertDate(data.expiresAt),
            },
          ]}
        />
        <div className="certification-detail__heading">
          <h2 className="certification-detail__name">{data.name}</h2>
          {data.issuer !== undefined && (
            <p className="certification-detail__issuer">{data.issuer}</p>
          )}
          {data.description !== undefined && (
            <p className="certification-detail__description">{data.description}</p>
          )}
          {data.skills.length > 0 && (
            <div className="certification-detail__skills">
              {data.skills.map((skill) => (
                <Badge key={skill.id} tone="neutral">
                  {skill.name}
                </Badge>
              ))}
            </div>
          )}
          {data.url !== undefined && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="certification-detail__link"
            >
              <Icon name="external" />
              <span>Vedi la certificazione</span>
            </a>
          )}

          {/* Assegnazione del membro: date di conseguimento/scadenza, origine e
              link al badge specifico. */}
          <dl className="certification-detail__meta">
            <div className="certification-detail__meta-item">
              <dt>Conseguita</dt>
              <dd>{formatCertDate(data.issuedAt)}</dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Scadenza</dt>
              <dd>
                <ExpiryValue iso={data.expiresAt} />
              </dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Origine</dt>
              <dd>
                {data.source === 'credly' ? (
                  <Badge tone="info">Credly</Badge>
                ) : (
                  <Badge tone="neutral">Manuale</Badge>
                )}
              </dd>
            </div>
            {data.badgeUrl !== undefined && (
              <div className="certification-detail__meta-item">
                <dt>Badge</dt>
                <dd>
                  <a
                    href={data.badgeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="certification-detail__link"
                  >
                    <Icon name="external" />
                    <span>Apri su Credly</span>
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </section>
  );
}
